import React, { useReducer, useEffect, Fragment } from 'react';
import validator from 'validator';
import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputBase from '@material-ui/core/InputBase';
import { observer } from 'mobx-react';

import CalendarCheckoutStore from '../../store/CalendarCheckoutStore';
import { getAnchors } from '../../pages/Anchors/services';
import { UserContext } from '../../context';
import { BASE_URL, FETCH_GENERATED_USERNAME, SEND_TO_PROCESSOR } from '../../constants/apiUrls/apiUrls';
import callApi from '../../utils/callApi';
import { CALENDAR_MAX_SHIPPING_PRICE, PROCESSOR_IDS, PROCESSOR_OPT_STATUS } from '../../constants/processors';

import { getCountries } from './services';
import ShippingCost from './ShippingCost/ShippingCost';


const initialState = {
  country: '',
  region: '',
  firstName: '',
  city: '',
  lastName: '',
  address: '',
  zipCode: '',
  anchor: '',
  checked: false,
  anchors: [],
  countries: [],
  valid: true,
  zipCodeValid: true,
};

function reducer(state, { field, value }) {
  return {
    ...state,
    [field]: value,
  };
}

const CustomInput = withStyles(() => {
  return {
    input: {
      borderRadius: 15,
      backgroundColor: '#fff',
      border: 'none',
      fontSize: 14,
      padding: '10px',
      '&:focus': { borderRadius: 15 },
    },
  };
})(InputBase);

function CalendarShippingForm(props) {
  const user = React.useContext(UserContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    anchors,
    countries,
    country,
    region,
    firstName,
    lastName,
    city,
    address,
    zipCode,
    anchor,
    checked,
    zipCodeValid,
  } = state;
  const supportedCountries = ['US', 'CA', 'GB', 'DE', 'FR', 'NL'];
  const fieldsInvalid = supportedCountries.includes(country) ? Object.values(state).some(
    (el) => el === null || el === ''
  ) && ['GB', 'DE', 'FR', 'NL'].includes(country) && region !== '' || !zipCodeValid : !country || !anchor;
  const regions = country && countries.find((el) => el.countryCode === country).regions;
  const isButtonDisabled = !checked || fieldsInvalid;
  const { finalProduct } = CalendarCheckoutStore;

  useEffect(() => {
    getCurrentAnchors();
    getShippingCountries();
  }, []);

  useEffect(() => {
    if (zipCode && zipCodeValid && country) {
      CalendarCheckoutStore.getShippingFee(zipCode, country);
    }
  }, [zipCode, zipCodeValid, country]);

  const getCurrentAnchors = async() => {
    const { error, data } = await getAnchors();
    if (!error) {
      dispatch({ field: 'anchors', value: data.data.currentAnchors });
    }
  };

  const getShippingCountries = async() => {
    const { error, data } = await getCountries();
    if (!error) {
      const sortedCountries = data.data.sort((a, b) => {
        if (a.countryName.toLowerCase() < b.countryName.toLowerCase()) {
          return -1;
        }
        if (a.countryName.toLowerCase() > b.countryName.toLowerCase()) {
          return 1;
        }

        return 0;
      });
      dispatch({ field: 'countries', value: sortedCountries });
    }
    if (!['', 'unknown'].includes(user.realCountryCode)) {
      dispatch({ field: 'country', value: user.realCountryCode });
    }
  };

  const onChange = (e) => {
    dispatch({ field: e.target.name, value: e.target.value });
  };

  const onCheckboxChange = (e) => {
    dispatch({ field: e.target.name, value: e.target.checked });
  };

  const onZipChange = (e) => {
    const { value, name } = e.target;
    const { country } = state;
    const isValid = validator.isPostalCode(value, country);
    // ['US','CA','GB', 'DE', 'FR', 'NL'].forEach(item => {
    // if(validator.isPostalCode(value, item)){
    //   isValid = true;
    // }
    // });
    dispatch({ field: 'zipCodeValid', value: isValid });
    dispatch({ field: name, value });
  };

  const handleClick = () => {
    const { firstName, lastName, address, anchor, city, country, region, zipCode } = state;
    const formattedLastName = country === 'FR' ? lastName.toUpperCase() : lastName;
    CalendarCheckoutStore.setShippingDetails({ firstName, lastName: formattedLastName, address, anchor, country, region, zipCode, city });
    // Checkout logic
    if (
      !['US', 'CA', 'GB', 'DE', 'FR', 'NL'].includes(CalendarCheckoutStore.shippingDetails.country) ||
      (CalendarCheckoutStore.error && CalendarCheckoutStore.error === 'PRODUCT_NOT_FOUND')
    ) {
      props.history.push(
        `/calendar-checkout?opStatus=${PROCESSOR_OPT_STATUS.NOT_IN_REGION}&shippingAnchorId=${anchor}`
      );
    } else {
      processRequest();
    }
  };

  const fetchUserName = () => callApi(FETCH_GENERATED_USERNAME, 'post', {}, {}, { 'content-type': 'application/json; charset=utf-8' }, false);

  const processRequest = () => {
    if (user.authenticated) {
      checkout(user.user.username);
    } else {
      fetchUserName().then((res) => {
        checkout(res.data.username);
      });
    }
  };

  const checkout = (username) => {
    if (CalendarCheckoutStore.finalProduct) {
      const { finalProduct: { paymentMethod, product }, shippingDetails } = CalendarCheckoutStore;
      const processorData = {
        originalDomain: BASE_URL || new URL(document.URL).origin,
        paymentType: paymentMethod,
        returnPath: '/calendar-checkout',
        email: user.authenticated ? user.user.account.email : '',
        shipping: {
          address: shippingDetails.address,
          countryCode: shippingDetails.country,
          zipCode: shippingDetails.zipCode,
          regionCode: shippingDetails.region,
          name: `${shippingDetails.firstName} ${shippingDetails.lastName}`,
          anchorId: shippingDetails.anchor,
          city,
        },
      };
      const url = SEND_TO_PROCESSOR.replace('{username}', encodeURIComponent(username)).
        replace('{processorId}', encodeURIComponent(PROCESSOR_IDS.SEGPAY)).
        replace('{priceId}', encodeURIComponent(product.id));

      callApi(url, 'post', {}, JSON.stringify(processorData), { 'content-type': 'application/json; charset=utf-8' }, false, false).then((processorRes) => {
        redirectToProcessor(processorRes.data.nextStage.url, processorRes.data.nextStage.parameters);
      });
    } else {
      console.log('Unable to read checkout information');
    }

  };

  const redirectToProcessor = (url, params) => {
    let buildUrl = `${url}?`;
    let parsedKey = '';

    for (const key in params) {
      if (!params.hasOwnProperty(key)) {
        continue;
      }
      parsedKey = `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}&`;
      buildUrl += parsedKey;
    }
    window.location.href = buildUrl;
  };

  return (
    <div className="calendar-shipping-form">
      <h4>SHIPPING DETAILS</h4>
      <div className="shipping-form-field">
        <span>Country</span>
        <FormControl className="shipping-form-dropdown">
          <NativeSelect
            input={<CustomInput />}
            name="country"
            value={country}
            onChange={onChange}
          >
            <option aria-label="None" value="" />
            {countries.map((el) => (
              <option key={el.countryCode} value={el.countryCode}>
                {el.countryName}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
      </div>
      {country && supportedCountries.includes(country) && (
        <Fragment>
          {country && ['US', 'CA'].includes(country) && (
            <div className="shipping-form-field">
              <span>State/Region</span>
              <FormControl className="shipping-form-dropdown">
                <NativeSelect
                  input={<CustomInput />}
                  name="region"
                  value={region}
                  onChange={onChange}
                >
                  <option aria-label="None" value="" />
                  {regions.map((el) => (
                    <option key={el.regionCode} value={el.regionCode}>
                      {el.regionName}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
            </div>
          )}
          <div className="shipping-form-field">
            <span>City</span>
            <input name="city" type="text" value={city} onChange={onChange} />
          </div>
          <div className="shipping-form-field">
            <span>First Name</span>
            <input name="firstName" type="text" value={firstName} onChange={onChange} />
          </div>
          <div className="shipping-form-field">
            <span>Last Name</span>
            <input name="lastName" type="text" value={lastName} onChange={onChange} />
          </div>
          <div className="shipping-form-field">
            <span>Delivery Address</span>
            <input
              name="address"
              type="text"
              value={address}
              onChange={onChange}
            />
          </div>
          <div className="shipping-form-field">
            <span>Zipcode</span>
            <input
              className={!zipCodeValid ? 'zip-margin' : undefined}
              name="zipCode"
              type="text"
              value={zipCode}
              onChange={onZipChange}
            />
            {!zipCodeValid && <p className="error-text zip-error">Zip Code is invalid</p>}
          </div>
        </Fragment>
      )}
      <div className="shipping-form-field">
        <span>Favorite Anchor</span>
        <FormControl className="shipping-form-dropdown">
          <NativeSelect
            input={<CustomInput />}
            name="anchor"
            value={anchor}
            onChange={onChange}
          >
            <option aria-label="None" value="" />
            {anchors.map((el) => (
              <option key={el.id} value={el.id}>
                {el.name}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
      </div>
      {zipCodeValid && <ShippingCost />}
      <button className="btn btn-red btn-calendar" disabled={isButtonDisabled} onClick={handleClick}>
        CONTINUE
      </button>
      <FormControlLabel
        className="shipping-form-checkbox"
        control={
          <Checkbox
            checked={checked}
            name="checked"
            onChange={onCheckboxChange}
          />
        }
        label="I confirm I am over 18 years old"
      />
    </div>
  );
}

export default observer(CalendarShippingForm);
